import { useCallback, useEffect, useRef, useState } from 'react';
import { SessionTranscriptionService } from '@/services/session-transcription.service';
import {
	captureTranscriptionAudio,
	createTranscriptionAudioCaptureFromStreams,
	type TranscriptionAudioCapture,
	type TranscriptionCaptureMode,
} from '@/utils/captureTranscriptionAudioStream';

const DEEPGRAM_LISTEN_URL = 'wss://api.deepgram.com/v1/listen';
const FRAGMENT_SYNC_INTERVAL_MS = 10_000;
const MEDIA_RECORDER_TIMESLICE_MS = 250;
const KEEP_ALIVE_INTERVAL_MS = 8_000;
const TOKEN_REFRESH_LEAD_MS = 5_000;

const DEEPGRAM_LISTEN_PARAMS = new URLSearchParams({
	model: 'nova-3',
	smart_format: 'true',
	interim_results: 'true',
	language: 'pt-BR',
});

type DeepgramConnectionStatus =
	| 'idle'
	| 'connecting'
	| 'connected'
	| 'reconnecting'
	| 'error'
	| 'stopped';

type UseDeepgramLiveTranscriptionOptions = {
	sessionId: string;
	enabled: boolean;
	sessionStartedAtMs?: number | null;
	onConnectionError?: (message: string) => void;
};

type TranscriptionStartStreams = {
	micStream: MediaStream;
	systemStream?: MediaStream | null;
};

type UseDeepgramLiveTranscriptionResult = {
	status: DeepgramConnectionStatus;
	captureMode: TranscriptionCaptureMode | null;
	micStream: MediaStream | null;
	systemStream: MediaStream | null;
	mixedStream: MediaStream | null;
	finalTranscript: string;
	interimTranscript: string;
	lastFinalSegment: string;
	finalSegmentCounter: number;
	isRecording: boolean;
	lastSyncAt: Date | null;
	lastError: string | null;
	start: (
		mode: TranscriptionCaptureMode,
		existingStreams?: TranscriptionStartStreams,
	) => void;
	retry: () => void;
	replaceSystemAudioSource: () => Promise<void>;
	removeSystemAudio: () => void;
	flushPendingFragments: () => Promise<void>;
};

const buildDeepgramWebSocketUrl = () =>
	`${DEEPGRAM_LISTEN_URL}?${DEEPGRAM_LISTEN_PARAMS.toString()}`;

const parseDeepgramMessage = (
	raw: unknown,
): SessionTranscription.DeepgramResultsMessage | null => {
	if (typeof raw !== 'string') return null;

	try {
		return JSON.parse(raw) as SessionTranscription.DeepgramResultsMessage;
	} catch {
		return null;
	}
};

const resolveModelInfo = (
	message: SessionTranscription.DeepgramResultsMessage,
): SessionTranscription.DeepgramModelInfo | undefined => {
	const modelInfo = message.metadata?.model_info;
	if (!modelInfo?.name) return undefined;

	return {
		name: modelInfo.name,
		version: modelInfo.version,
		uuid: modelInfo.uuid,
	};
};

export const useDeepgramLiveTranscription = ({
	sessionId,
	enabled,
	sessionStartedAtMs,
	onConnectionError,
}: UseDeepgramLiveTranscriptionOptions): UseDeepgramLiveTranscriptionResult => {
	const [status, setStatus] = useState<DeepgramConnectionStatus>('idle');
	const [captureMode, setCaptureMode] =
		useState<TranscriptionCaptureMode | null>(null);
	const [micStream, setMicStream] = useState<MediaStream | null>(null);
	const [systemStream, setSystemStream] = useState<MediaStream | null>(null);
	const [mixedStream, setMixedStream] = useState<MediaStream | null>(null);
	const [finalTranscript, setFinalTranscript] = useState('');
	const [interimTranscript, setInterimTranscript] = useState('');
	const [lastFinalSegment, setLastFinalSegment] = useState('');
	const [finalSegmentCounter, setFinalSegmentCounter] = useState(0);
	const [isRecording, setIsRecording] = useState(false);
	const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
	const [lastError, setLastError] = useState<string | null>(null);

	const finalTranscriptRef = useRef('');
	const interimTranscriptRef = useRef('');
	const syncedTranscriptLengthRef = useRef(0);
	const requestIdRef = useRef<string | null>(null);
	const requestIdSyncedForConnectionRef = useRef(false);
	const modelInfoRef = useRef<
		SessionTranscription.DeepgramModelInfo | undefined
	>(undefined);
	const wsRef = useRef<WebSocket | null>(null);
	const audioCaptureRef = useRef<TranscriptionAudioCapture | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const keepAliveIntervalRef = useRef<number | null>(null);
	const fragmentSyncIntervalRef = useRef<number | null>(null);
	const tokenExpiresAtRef = useRef<number | null>(null);
	const accessTokenRef = useRef<string | null>(null);
	const sessionStartedAtMsRef = useRef(sessionStartedAtMs ?? Date.now());
	const enabledRef = useRef(enabled);
	const onConnectionErrorRef = useRef(onConnectionError);
	const isStartingRef = useRef(false);
	const isSyncingRef = useRef(false);
	const captureModeRef = useRef<TranscriptionCaptureMode | null>(null);
	const existingStreamsRef = useRef<TranscriptionStartStreams | undefined>(
		undefined,
	);

	enabledRef.current = enabled;
	onConnectionErrorRef.current = onConnectionError;
	captureModeRef.current = captureMode;

	useEffect(() => {
		if (sessionStartedAtMs != null) {
			sessionStartedAtMsRef.current = sessionStartedAtMs;
		}
	}, [sessionStartedAtMs]);

	const syncCaptureStreams = useCallback(
		(capture: TranscriptionAudioCapture | null) => {
			setMicStream(capture?.micStream ?? null);
			setSystemStream(capture?.systemStream ?? null);
			setMixedStream(capture?.stream ?? null);
		},
		[],
	);

	const resolveEstimatedDurationSeconds = useCallback((): number => {
		const elapsedMs = Date.now() - sessionStartedAtMsRef.current;
		return Math.max(0, Math.floor(elapsedMs / 1000));
	}, []);

	const pushPendingFragments = useCallback(async () => {
		if (isSyncingRef.current) return;

		const fullTranscript = finalTranscriptRef.current;
		const unsentContent = fullTranscript.slice(
			syncedTranscriptLengthRef.current,
		);
		if (!unsentContent.trim() || !enabledRef.current) return;

		isSyncingRef.current = true;

		const body: SessionTranscription.PatchFragmentsBody = {
			content: unsentContent,
			estimated_duration_seconds: resolveEstimatedDurationSeconds(),
			...(modelInfoRef.current
				? { model_info: modelInfoRef.current }
				: {}),
		};

		if (requestIdRef.current && !requestIdSyncedForConnectionRef.current) {
			body.request_id = requestIdRef.current;
		}

		try {
			await SessionTranscriptionService.patchFragments(sessionId, body);
			syncedTranscriptLengthRef.current = fullTranscript.length;
			if (body.request_id) {
				requestIdSyncedForConnectionRef.current = true;
			}
			setLastSyncAt(new Date());
		} catch {
			// sync falhou — próximo intervalo tenta de novo
		} finally {
			isSyncingRef.current = false;
		}
	}, [resolveEstimatedDurationSeconds, sessionId]);

	const clearKeepAlive = useCallback(() => {
		if (keepAliveIntervalRef.current != null) {
			window.clearInterval(keepAliveIntervalRef.current);
			keepAliveIntervalRef.current = null;
		}
	}, []);

	const clearFragmentSync = useCallback(() => {
		if (fragmentSyncIntervalRef.current != null) {
			window.clearInterval(fragmentSyncIntervalRef.current);
			fragmentSyncIntervalRef.current = null;
		}
	}, []);

	const stopMediaCapture = useCallback(() => {
		const recorder = mediaRecorderRef.current;
		if (recorder && recorder.state !== 'inactive') {
			recorder.stop();
		}
		mediaRecorderRef.current = null;

		audioCaptureRef.current?.release();
		audioCaptureRef.current = null;
		syncCaptureStreams(null);
		setIsRecording(false);
	}, [syncCaptureStreams]);

	const closeWebSocket = useCallback(() => {
		clearKeepAlive();
		const ws = wsRef.current;
		if (ws && ws.readyState === WebSocket.OPEN) {
			try {
				ws.send(JSON.stringify({ type: 'CloseStream' }));
			} catch {
				// ignore
			}
		}
		ws?.close();
		wsRef.current = null;
	}, [clearKeepAlive]);

	const reportError = useCallback((message: string) => {
		setLastError(message);
		setStatus('error');
		onConnectionErrorRef.current?.(message);
	}, []);

	const fetchAccessToken = useCallback(async (): Promise<string> => {
		const response = await SessionTranscriptionService.postToken(sessionId);
		tokenExpiresAtRef.current =
			Date.now() +
			Math.max(0, response.expires_in * 1000 - TOKEN_REFRESH_LEAD_MS);
		accessTokenRef.current = response.access_token;
		return response.access_token;
	}, [sessionId]);

	const handleDeepgramMessage = useCallback((event: MessageEvent<string>) => {
		const message = parseDeepgramMessage(event.data);
		if (!message) return;

		if (message.metadata?.request_id && !requestIdRef.current) {
			requestIdRef.current = message.metadata.request_id;
		}

		const modelInfo = resolveModelInfo(message);
		if (modelInfo) {
			modelInfoRef.current = modelInfo;
		}

		const transcript =
			message.channel?.alternatives?.[0]?.transcript?.trim();
		if (!transcript) return;

		if (message.is_final) {
			const nextFinal = finalTranscriptRef.current
				? `${finalTranscriptRef.current} ${transcript}`
				: transcript;
			finalTranscriptRef.current = nextFinal;
			interimTranscriptRef.current = '';
			setFinalTranscript(nextFinal);
			setInterimTranscript('');
			setLastFinalSegment(transcript);
			setFinalSegmentCounter((current) => current + 1);
			return;
		}

		interimTranscriptRef.current = transcript;
		setInterimTranscript(transcript);
	}, []);

	const attachRecorder = useCallback(
		(ws: WebSocket, capture: TranscriptionAudioCapture) => {
			try {
				const stream = capture.stream;
				const mimeTypes = [
					'audio/webm;codecs=opus',
					'audio/webm',
					'audio/ogg;codecs=opus',
					'audio/mp4',
					'',
				];
				let mimeType = '';
				for (const type of mimeTypes) {
					if (type === '' || MediaRecorder.isTypeSupported(type)) {
						mimeType = type;
						break;
					}
				}
				const recorder = mimeType
					? new MediaRecorder(stream, { mimeType })
					: new MediaRecorder(stream);
				mediaRecorderRef.current = recorder;

				recorder.addEventListener('dataavailable', (event) => {
					if (
						event.data.size > 0 &&
						ws.readyState === WebSocket.OPEN &&
						wsRef.current === ws
					) {
						ws.send(event.data);
					}
				});

				recorder.start(MEDIA_RECORDER_TIMESLICE_MS);
				setIsRecording(true);
			} catch (error: unknown) {
				closeWebSocket();
				stopMediaCapture();
				reportError(
					error instanceof Error
						? error.message
						: 'Não foi possível iniciar o gravador de áudio.',
				);
			}
		},
		[closeWebSocket, stopMediaCapture, reportError],
	);

	const beginTranscription = useCallback(
		async (
			mode: TranscriptionCaptureMode,
			existingStreams?: TranscriptionStartStreams,
		) => {
			if (!enabledRef.current || isStartingRef.current) return;

			isStartingRef.current = true;
			existingStreamsRef.current = existingStreams;
			setLastError(null);
			setCaptureMode(mode);
			accessTokenRef.current = null;
			requestIdRef.current = null;
			requestIdSyncedForConnectionRef.current = false;
			setStatus('connecting');

			closeWebSocket();
			stopMediaCapture();

			try {
				const capture = existingStreams?.micStream
					? createTranscriptionAudioCaptureFromStreams(
							existingStreams.micStream,
							mode === 'microphone-and-system'
								? existingStreams.systemStream ?? null
								: null,
					  )
					: await captureTranscriptionAudio(mode);
				audioCaptureRef.current = capture;
				syncCaptureStreams(capture);
				capture.onSystemAudioEnded(() => {
					if (!enabledRef.current) return;
					closeWebSocket();
					stopMediaCapture();
					reportError(
						'Compartilhamento de tela encerrado. Clique em Tentar novamente.',
					);
				});

				const accessToken = await fetchAccessToken();

				const ws = new WebSocket(buildDeepgramWebSocketUrl(), [
					'bearer',
					accessToken,
				]);
				wsRef.current = ws;
				ws.binaryType = 'arraybuffer';

				ws.addEventListener('open', () => {
					if (wsRef.current !== ws) return;
					setStatus('connected');
					setLastError(null);
					attachRecorder(ws, capture);
				});

				ws.addEventListener('message', handleDeepgramMessage);

				ws.addEventListener('error', () => {
					if (wsRef.current !== ws) return;
					closeWebSocket();
					stopMediaCapture();
					reportError('Erro na conexão com a transcrição.');
				});

				ws.addEventListener('close', () => {
					if (wsRef.current !== ws) return;
					stopMediaCapture();
					if (!enabledRef.current) {
						setStatus('stopped');
						return;
					}
					reportError(
						'Conexão com transcrição perdida. Clique em Tentar novamente.',
					);
				});

				keepAliveIntervalRef.current = window.setInterval(() => {
					if (ws.readyState !== WebSocket.OPEN) return;
					ws.send(JSON.stringify({ type: 'KeepAlive' }));
				}, KEEP_ALIVE_INTERVAL_MS);
			} catch (error: unknown) {
				stopMediaCapture();
				setCaptureMode(null);
				const message =
					error instanceof Error
						? error.message
						: 'Não foi possível capturar o áudio da sessão.';
				reportError(message);
			} finally {
				isStartingRef.current = false;
			}
		},
		[
			attachRecorder,
			closeWebSocket,
			fetchAccessToken,
			handleDeepgramMessage,
			reportError,
			stopMediaCapture,
			syncCaptureStreams,
		],
	);

	const start = useCallback(
		(
			mode: TranscriptionCaptureMode,
			existingStreams?: TranscriptionStartStreams,
		) => {
			void beginTranscription(mode, existingStreams);
		},
		[beginTranscription],
	);

	const retry = useCallback(() => {
		const mode = captureModeRef.current ?? 'microphone-and-system';
		void beginTranscription(mode, existingStreamsRef.current);
	}, [beginTranscription]);

	const replaceSystemAudioSource = useCallback(async () => {
		const capture = audioCaptureRef.current;
		if (!capture) return;

		try {
			await capture.replaceSystemAudio();
			syncCaptureStreams(capture);
			setCaptureMode('microphone-and-system');
		} catch (error: unknown) {
			const message =
				error instanceof Error
					? error.message
					: 'Não foi possível trocar a fonte de áudio.';
			reportError(message);
		}
	}, [reportError, syncCaptureStreams]);

	const removeSystemAudio = useCallback(() => {
		const capture = audioCaptureRef.current;
		if (!capture?.systemStream) return;

		capture.removeSystemAudio();
		syncCaptureStreams(capture);
		setCaptureMode('microphone');
	}, [syncCaptureStreams]);

	useEffect(() => {
		if (!enabled) {
			setStatus('stopped');
			setCaptureMode(null);
			clearFragmentSync();
			closeWebSocket();
			stopMediaCapture();
			return;
		}

		setStatus((current) => (current === 'stopped' ? 'idle' : current));

		fragmentSyncIntervalRef.current = window.setInterval(() => {
			void pushPendingFragments();
		}, FRAGMENT_SYNC_INTERVAL_MS);

		return () => {
			clearFragmentSync();
			closeWebSocket();
			stopMediaCapture();
		};
	}, [
		clearFragmentSync,
		closeWebSocket,
		enabled,
		stopMediaCapture,
		pushPendingFragments,
	]);

	return {
		status,
		captureMode,
		micStream,
		systemStream,
		mixedStream,
		finalTranscript,
		interimTranscript,
		lastFinalSegment,
		finalSegmentCounter,
		isRecording,
		lastSyncAt,
		lastError,
		start,
		retry,
		replaceSystemAudioSource,
		removeSystemAudio,
		flushPendingFragments: pushPendingFragments,
	};
};

export type {
	DeepgramConnectionStatus,
	TranscriptionStartStreams,
	UseDeepgramLiveTranscriptionResult,
};
